import type { TitleBrief } from "@anisync/types";
import { CompassIcon, InfoIcon, RotateCcwIcon } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { TitleCard } from "../components/title-card";
import { Alert, AlertDescription } from "../components/ui/alert";
import { Button } from "../components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";
import { ApiError, type BrowseParams, browseTitles } from "../lib/api";

/**
 * Przeglądanie katalogu bez znajomości tytułu.
 *
 * Do tej pory jedyną drogą do czegokolwiek było wyszukiwanie, czyli **trzeba było
 * wiedzieć, czego się chce**. Ten ekran jest odpowiedzią na „nie wiem, chcę coś
 * zobaczyć".
 *
 * Pytamy AniLista, nie naszego lustra: lustro rośnie z tego, co ktoś już wyszukał,
 * więc „popularne w tym sezonie" złożone z tego podzbioru byłoby listą mylącą, a nie
 * niepełną. Wyniki wpadają przy okazji do lustra, więc każde przeglądanie poszerza
 * wyszukiwanie lokalne.
 */

const SKELETON_KEYS = ["s1", "s2", "s3", "s4", "s5", "s6", "s7", "s8"] as const;

const SEASONS = [
  { value: "WINTER", label: "Zima" },
  { value: "SPRING", label: "Wiosna" },
  { value: "SUMMER", label: "Lato" },
  { value: "FALL", label: "Jesień" },
] as const;

const FORMATS = [
  { value: "TV", label: "Serial" },
  { value: "TV_SHORT", label: "Krótkometrażowy" },
  { value: "MOVIE", label: "Film" },
  { value: "OVA", label: "OVA" },
  { value: "ONA", label: "ONA" },
  { value: "SPECIAL", label: "Specjalny" },
] as const;

const SORTS = [
  { value: "POPULARITY_DESC", label: "Najpopularniejsze" },
  { value: "SCORE_DESC", label: "Najwyżej oceniane" },
  { value: "TRENDING_DESC", label: "Na fali" },
  { value: "START_DATE_DESC", label: "Najnowsze" },
] as const;

/** Wartość dla „bez filtra". Puste `value` w Radiksie znaczy brak zaznaczenia. */
const ANY = "any";

/**
 * Stan filtrów trzymamy z `null`, nie z `undefined`.
 *
 * `exactOptionalPropertyTypes` w tsconfigu odrzuca podanie `undefined` do pola
 * opcjonalnego, a „bez filtra" to realny stan, który trzeba dać się ustawić.
 * `null` mówi to wprost; zamiana na kształt żądania jest jednym miejscem niżej.
 */
interface BrowseState {
  season: NonNullable<BrowseParams["season"]> | null;
  year: number | null;
  genre: string | null;
  format: NonNullable<BrowseParams["format"]> | null;
  sort: NonNullable<BrowseParams["sort"]>;
  page: number;
}

const EMPTY_STATE: BrowseState = {
  season: null,
  year: null,
  genre: null,
  format: null,
  sort: "POPULARITY_DESC",
  page: 1,
};

function toParams(state: BrowseState): BrowseParams {
  return {
    ...(state.season !== null ? { season: state.season } : {}),
    ...(state.year !== null ? { year: state.year } : {}),
    ...(state.genre !== null ? { genre: state.genre } : {}),
    ...(state.format !== null ? { format: state.format } : {}),
    sort: state.sort,
    page: state.page,
  };
}

export function BrowseScreen() {
  const [items, setItems] = useState<TitleBrief[] | null>(null);
  const [genres, setGenres] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  /** Zdanie od serwera o tym, że widok jest zastępczy — nie błąd, więc nie czerwień. */
  const [notice, setNotice] = useState<string | null>(null);
  const [state, setState] = useState<BrowseState>(EMPTY_STATE);

  const load = useCallback((next: BrowseParams) => {
    setItems(null);
    setError(null);
    setNotice(null);
    browseTitles(next)
      .then((data) => {
        setItems(data.items);
        setNotice(data.notice);
        // Gatunki przychodzą z każdą odpowiedzią, ale podmieniamy je tylko wtedy,
        // gdy są: pusta lista po nieudanym żądaniu zabrałaby filtry z ekranu.
        if (data.genres.length > 0) setGenres(data.genres);
      })
      .catch((e: unknown) => {
        setItems([]);
        setError(e instanceof ApiError ? e.message : "Nie udało się wczytać katalogu");
      });
  }, []);

  useEffect(() => {
    load(toParams(state));
  }, [state, load]);

  const patch = useCallback((change: Partial<BrowseState>) => {
    // Każda zmiana filtru wraca na pierwszą stronę: piąta strona wyników dla
    // innego zestawu filtrów to pustka albo przypadkowy fragment.
    setState((prev) => ({ ...prev, ...change, page: 1 }));
  }, []);

  const year = new Date().getFullYear();
  const years = Array.from({ length: 26 }, (_, i) => year + 1 - i);
  const filtered =
    state.season !== null || state.year !== null || state.genre !== null || state.format !== null;

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-col gap-1">
        <h1 className="flex items-center gap-2 text-xl font-semibold">
          <CompassIcon className="size-5 text-accent" />
          Przeglądaj
        </h1>
        <p className="text-sm text-muted-foreground">
          Katalog po sezonie, gatunku i formacie — na wypadek, gdy nie wiesz, czego szukasz.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <FilterSelect
          label="Sezon"
          value={state.season ?? ANY}
          options={SEASONS.map((s) => ({ value: s.value, label: s.label }))}
          onChange={(value) =>
            patch({ season: value === ANY ? null : (value as BrowseState["season"]) })
          }
        />
        <FilterSelect
          label="Rok"
          value={state.year !== null ? String(state.year) : ANY}
          options={years.map((y) => ({ value: String(y), label: String(y) }))}
          onChange={(value) => patch({ year: value === ANY ? null : Number.parseInt(value, 10) })}
        />
        <FilterSelect
          label="Gatunek"
          value={state.genre ?? ANY}
          options={genres.map((g) => ({ value: g, label: g }))}
          onChange={(value) => patch({ genre: value === ANY ? null : value })}
        />
        <FilterSelect
          label="Format"
          value={state.format ?? ANY}
          options={FORMATS.map((f) => ({ value: f.value, label: f.label }))}
          onChange={(value) =>
            patch({ format: value === ANY ? null : (value as BrowseState["format"]) })
          }
        />

        <Select
          value={state.sort}
          onValueChange={(value) => patch({ sort: value as BrowseState["sort"] })}
        >
          <SelectTrigger className="w-[12rem]" aria-label="Kolejność">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {SORTS.map((s) => (
              <SelectItem key={s.value} value={s.value}>
                {s.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {filtered && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setState({ ...EMPTY_STATE, sort: state.sort })}
          >
            <RotateCcwIcon />
            Wyczyść filtry
          </Button>
        )}
      </div>

      {error !== null && (
        <Alert variant="destructive">
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}

      {/* `variant="info"`, nie `destructive`: widok zastępczy nie jest awarią, tylko
          niepełną odpowiedzią. `aria-live`, bo zdanie pojawia się po żądaniu, którego
          widz nie inicjował świadomie — zmiana byłaby wyłącznie wizualna. */}
      {notice !== null && error === null && (
        <Alert variant="info" aria-live="polite">
          <InfoIcon />
          <AlertDescription>{notice}</AlertDescription>
        </Alert>
      )}

      {items === null && (
        <div className="tile-grid gap-x-3 gap-y-4">
          {SKELETON_KEYS.map((k) => (
            <Skeleton key={k} className="aspect-[2/3] rounded-xl" />
          ))}
        </div>
      )}

      {items !== null && items.length === 0 && error === null && (
        <p className="text-muted-foreground">
          Nic nie odpowiada tym filtrom. Spróbuj szerszego zakresu — na przykład bez sezonu.
        </p>
      )}

      {items !== null && items.length > 0 && (
        <>
          <div className="tile-grid gap-x-3 gap-y-4">
            {items.map((title, i) => (
              <TitleCard key={title.id} title={title} fluid index={i} />
            ))}
          </div>

          {/*
            Strony liczymy w przód bez znajomości ich liczby: AniList podaje ją
            w `pageInfo`, ale przy filtrach i tak jest szacunkiem, a przycisk
            „następna", który prowadzi w pustkę, jest mniej mylący niż numer strony
            obiecujący zawartość.
          */}
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              disabled={state.page <= 1}
              onClick={() => setState((prev) => ({ ...prev, page: prev.page - 1 }))}
            >
              Poprzednia
            </Button>
            <span className="text-sm tabular-nums text-muted-foreground">strona {state.page}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setState((prev) => ({ ...prev, page: prev.page + 1 }))}
            >
              Następna
            </Button>
          </div>
        </>
      )}
    </div>
  );
}

/**
 * Pojedynczy filtr z pozycją „dowolny".
 *
 * Wydzielony, bo cztery kopie tego samego układu (etykieta, lista, pozycja
 * zerująca) rozjechałyby się przy pierwszej zmianie któregokolwiek z nich.
 */
function FilterSelect({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: string;
  options: ReadonlyArray<{ value: string; label: string }>;
  onChange: (value: string) => void;
}) {
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="w-[10rem]" aria-label={label}>
        <SelectValue placeholder={label} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={ANY}>{label}: dowolny</SelectItem>
        {options.map((option) => (
          <SelectItem key={option.value} value={option.value}>
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}
